import React, { useState, useEffect } from "react";
import { Divider } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import TrashIcon from "../../../../Common/Svgs/TrashIcon";
import { updateBatteries } from "../../../../Redux/Slices/batteries.slice";
import {
  saveSmartContainerLayout,
  getSmartContainerLayout,
} from "../../../../Redux/APIs/smartContainersAPI";
import CannotDeleteLockerModal from "./CannotDeleteLockerModal";

/**
 * Helper to generate alphabetical label from index (e.g. 0 -> A, 25 -> Z)
 */
const getAlphabetLabel = (index) => {
  let label = "";
  let temp = index;
  while (temp >= 0) {
    label = String.fromCharCode((temp % 26) + 65) + label;
    temp = Math.floor(temp / 26) - 1;
  }
  return label;
};

const ContainerGrid = ({
  gridRows,
  gridColumns,
  lockers = [],
  rowFormat,
  columnFormat,
  deletedCells,
  setDeletedCells,
  id,
  activeSerialField,
}) => {
  const dispatch = useDispatch();
  const [hoveredCell, setHoveredCell] = useState(null);

  const isRowAlphabet = rowFormat.includes("A");
  const isColAlphabet = columnFormat.includes("A");

  const rowLabels = Array.from({ length: gridRows }, (_, rIndex) =>
    isRowAlphabet ? getAlphabetLabel(rIndex) : `${rIndex + 1}`,
  );

  const colLabels = Array.from({ length: gridColumns }, (_, cIndex) =>
    isColAlphabet ? getAlphabetLabel(cIndex) : `${cIndex + 1}`,
  );

  const handleDelete = (cellId) => {
    const locker = lockers.find((item) => item.lockerId === cellId);

    // Enforce front-end constraint: can't delete locker that has a battery (status !== 1, i.e., not AVAILABLE)
    if (locker && Number(locker.status) !== 1) {
      dispatch(
        updateBatteries({ key: "cannotDeleteLockerModal", value: true }),
      );
      return;
    }

    setDeletedCells((prev) =>
      prev.includes(cellId) ? prev : [...prev, cellId],
    );
  };

  const handleAdd = (cellId) => {
    setDeletedCells((prev) => prev.filter((cId) => cId !== cellId));
  };

  return (
    <div
      className={`bg-[#DFDFDF] p-2.5 h-full overflow-y-auto border-2 transition-colors duration-200
    ${activeSerialField === "container" ? "border-primary" : "border-[#DFDFDF]"}
  `}
    >
      {" "}
      <div
        className="grid gap-2.5 h-full"
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, minmax(50px, 130px))`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          justifyContent: "center",
        }}
      >
        {rowLabels.flatMap((rowLabel) =>
          colLabels.map((colLabel) => {
            const cellId = `${rowLabel}${colLabel}`;
            const isHovered = hoveredCell === cellId;
            const isDeleted = deletedCells.includes(cellId);

            const locker = lockers.find((item) => item.lockerId === cellId) || {
              lockerId: cellId,
              status: 1,
            };

            const isLockerEmpty = Number(locker.status) === 1;

            const showSecondImage = id && isLockerEmpty;

            let cellClassModifier = showSecondImage
              ? "bg-[#F5EDD5]"
              : "bg-[#F7F6F6]";

            let cellContent = showSecondImage ? (
              <div className="flex flex-col justify-center items-center gap-2 -mt-7">
                <div className="flex gap-x-0.5 justify-center mb-2.5">
                  <img
                    src="/assets/icons/battery/battery-disable.svg"
                    className="w-3 h-3"
                    alt=""
                  />

                  <p className="font-aileron font-semibold text-[10px] leading-none">
                    No Battery
                  </p>
                </div>
                <span className="w-7 h-6.5 p-1.5 rounded-lg flex items-center justify-center font-aileron font-semibold text-xs leading-none text-[#2F2F2F] bg-white">
                  {cellId}
                </span>
              </div>
            ) : (
              <span className="w-7 h-6.5 p-1.5 rounded-lg flex items-center justify-center font-aileron font-semibold text-xs leading-none text-[#2F2F2F] bg-white">
                {cellId}
              </span>
            );

            const isActiveLocker = activeSerialField === cellId;
            if (isActiveLocker) {
              cellClassModifier +=
                " border-2 border-primary shadow-[0_0_0_1px_rgba(0,146,255,0.15)]";
            }

            if (isDeleted && isHovered) {
              cellClassModifier =
                "bg-transparent border-2 border-dashed border-dark-gray";
              cellContent = (
                <div
                  className="flex flex-col items-center justify-center gap-2 cursor-pointer"
                  onClick={() => handleAdd(cellId)}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#FCFCFC]">
                    <img
                      src="/assets/icons/battery/add.svg"
                      className="w-4 h-4"
                      alt=""
                    />
                  </div>

                  <span className="font-aileron font-semibold text-xs leading-none text-black">
                    Add locker
                  </span>
                </div>
              );
            } else if (isDeleted) {
              cellClassModifier =
                "bg-transparent border-2 border-dashed border-dark-gray";
              cellContent = null;
            } else if (isHovered) {
              cellClassModifier = "bg-[#FFE9E4] border border-[#FFB29E]";
              cellContent = (
                <div
                  className="flex flex-col items-center justify-center gap-2 cursor-pointer"
                  onClick={() => handleDelete(cellId)}
                >
                  <div className="w-8 h-8 rounded-lg bg-alert-red flex items-center justify-center">
                    <TrashIcon className="text-white" />
                  </div>

                  <span className="font-aileron font-semibold text-xs leading-none text-black">
                    Delete locker
                  </span>
                </div>
              );
            }

            return (
              <div
                key={cellId}
                onMouseEnter={() => setHoveredCell(cellId)}
                onMouseLeave={() => setHoveredCell(null)}
                className={`
                  relative flex items-center justify-center min-h-0 transition-all duration-200 flex-1
                  ${cellClassModifier}
                `}
              >
                {cellContent}
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
};

const StepTwo = ({
  rows,
  columns,
  rowFormat,
  columnFormat,
  handleSave,
  id,
  setActiveSerialField,
  containerSerial,
  setContainerSerial,
  lockerSerials,
  setLockerSerials,
  deletedCells,
}) => {
  const navigate = useNavigate();

  const isRowAlphabet = rowFormat.includes("A");
  const isColAlphabet = columnFormat.includes("A");

  const rowLabels = Array.from({ length: rows }, (_, r) =>
    isRowAlphabet ? getAlphabetLabel(r) : `${r + 1}`,
  );

  const colLabels = Array.from({ length: columns }, (_, c) =>
    isColAlphabet ? getAlphabetLabel(c) : `${c + 1}`,
  );

  const lockers = rowLabels.flatMap((row) =>
    colLabels.map((col) => `${row}${col}`),
  );

  console.log({ deletedCells });

  return (
    <>
      <div className="border border-light-grey rounded-2xl p-3">
        <div className="bg-yellow rounded-lg py-1.5 px-3.5 h-[35px] flex items-center">
          <p className="font-poppins font-medium text-sm">Step 2</p>
        </div>

        <div className="mt-4">
          <p className="font-poppins font-medium text-sm">Serial numbers</p>

          <p className="mt-2 text-xs font-poppins font-light text-dark-gray leading-125">
            Enter the serial numbers for each locker and the container exactly
            as they appear on the labels.
          </p>
        </div>

        <Divider className="my-4" />

        <div className="space-y-3  pr-1">
          <div>
            <label
              htmlFor="container-serial"
              className="font-poppins text-xs font-medium text-dark-gray"
            >
              Container
              <input
                id="container-serial"
                value={containerSerial}
                onChange={(e) => setContainerSerial(e.target.value)}
                onFocus={() => setActiveSerialField("container")}
                onBlur={() => setActiveSerialField(null)}
                className="w-full h-9 rounded-md border border-light-grey px-3 focus:border-primary outline-none mt-1.5 placeholder:font-aileron
                      placeholder:font-normal
                      placeholder:text-xs
                      placeholder:leading-none
                      placeholder:capitalize
                      placeholder:text-dark-gray"
                placeholder="Enter Serial Number"
              />
            </label>
          </div>

          <Divider className="my-4" />

          {lockers
            .filter((locker) => !deletedCells.includes(locker))
            .map((locker) => (
              <div key={locker}>
                <label
                  htmlFor={`locker-${locker}-serial`}
                  className="font-poppins font-medium text-xs text-dark-gray"
                >
                  Locker {locker}
                </label>

                <input
                  id={`locker-${locker}-serial`}
                  value={lockerSerials[locker] || ""}
                  onChange={(e) =>
                    setLockerSerials((prev) => ({
                      ...prev,
                      [locker]: e.target.value,
                    }))
                  }
                  onFocus={() => setActiveSerialField(locker)}
                  onBlur={() => setActiveSerialField(null)}
                  className="
                      w-full
                      h-9
                      rounded-md
                      border
                      border-light-grey
                      px-3
                      focus:border-primary
                      focus:outline-none

                      font-aileron
                      font-semibold
                      text-xs
                      leading-none
                      capitalize

                      placeholder:font-aileron
                      placeholder:font-normal
                      placeholder:text-xs
                      placeholder:leading-none
                      placeholder:capitalize
                      placeholder:text-dark-gray mt-1.5
                    "
                  placeholder="Enter Serial Number"
                />
              </div>
            ))}
        </div>
      </div>

      {id ? (
        <>
          <span
            className="w-full mt-4 rounded-md px-4 py-[11px] bg-[#085054] hover:bg-[#528587] flex gap-x-2 items-center align-middle justify-center cursor-pointer"
            onClick={handleSave}
          >
            <img src="/images/tick.svg" className="w-4 h-4 text-white" alt="" />
            <span className="text-white font-poppins font-medium text-xs leading-none">
              Save changes
            </span>
          </span>
          <span
            className="w-full mt-2 mb-6 rounded-md px-4 py-[11px] bg-background hover:bg-background-grey flex gap-x-2 items-center align-middle justify-center cursor-pointer"
            onClick={() => navigate(`/batteries/smart-containers`)}
          >
            <span className="text-black font-poppins font-medium text-xs leading-none">
              Cancel
            </span>
          </span>
        </>
      ) : (
        <div className="w-full mt-4 mb-6 rounded-md px-4 py-[11px] bg-[#085054] hover:bg-[#528587] flex  justify-center cursor-pointer">
          <button
            type="button"
            onClick={handleSave}
            className=" flex gap-x-2 items-center align-middle"
          >
            <img
              src="/assets/icons/add.svg"
              className="w-4 h-4 text-white"
              alt=""
            />
            <span className="text-white font-poppins font-medium text-xs leading-none">
              Setup Container
            </span>
          </button>
        </div>
      )}
    </>
  );
};

function SetupContainerView({
  rows,
  columns,
  setRows,
  setColumns,
  id,
  lockers = [],
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { cannotDeleteLockerModal, smartContainerLayout } = useSelector(
    (state) => state.batteries,
  );

  const selectedHub = smartContainerLayout?.hubId;
  const existingLayout = smartContainerLayout?.layout;

  const [columnFormat, setColumnFormat] = useState("1,2,3..");
  const [rowFormat, setRowFormat] = useState("A,B,C..");
  const [deletedCells, setDeletedCells] = useState([]);
  const [currentStep, setCurrentStep] = useState(id ? 2 : 1);
  const [activeSerialField, setActiveSerialField] = useState(null);
  const [containerSerial, setContainerSerial] = useState("");
  const [lockerSerials, setLockerSerials] = useState({});

  // Initialize naming formats and deleted cells if layout exists
  useEffect(() => {
    if (existingLayout) {
      if (existingLayout.rowNamingFormat) {
        setRowFormat(existingLayout.rowNamingFormat);
      }
      if (existingLayout.colNamingFormat) {
        setColumnFormat(existingLayout.colNamingFormat);
      }
      if (existingLayout.disabledLockers) {
        setDeletedCells(existingLayout.disabledLockers);
      }
    }
    // Pre-populate serial numbers from existing layout data
    if (smartContainerLayout?.containerSerialNumber) {
      setContainerSerial(smartContainerLayout.containerSerialNumber);
    }
    if (smartContainerLayout?.lockers?.length) {
      const serialMap = {};
      smartContainerLayout.lockers.forEach((locker) => {
        if (locker.lockerSerialNumber) {
          serialMap[locker.lockerId] = locker.lockerSerialNumber;
        }
      });
      setLockerSerials(serialMap);
    }
  }, [existingLayout, smartContainerLayout]);

  const swapNamingFormat = () => {
    const prevCol = columnFormat;
    const prevRow = rowFormat;
    setColumnFormat(prevRow);
    setRowFormat(prevCol);
  };

  const handleDecrementRows = () => {
    if (rows <= 0) return;
    const isRowAlphabet = rowFormat.includes("A");
    const lastRowIndex = rows - 1;
    const lastRowLabel = isRowAlphabet
      ? getAlphabetLabel(lastRowIndex)
      : `${lastRowIndex + 1}`;

    const isColAlphabet = columnFormat.includes("A");
    const colLabels = Array.from({ length: columns }, (_, cIndex) =>
      isColAlphabet ? getAlphabetLabel(cIndex) : `${cIndex + 1}`,
    );

    // Check if any locker in the last row contains a battery
    const rowHasBattery = colLabels.some((colLabel) => {
      const cellId = `${lastRowLabel}${colLabel}`;
      const locker = lockers.find((item) => item.lockerId === cellId);
      return locker && Number(locker.status) !== 1; // 1 is LOCKER_STATUS.AVAILABLE
    });

    if (rowHasBattery) {
      dispatch(
        updateBatteries({ key: "cannotDeleteLockerModal", value: true }),
      );
      return;
    }

    setRows((prev) => Math.max(0, prev - 1));
  };

  const handleRowsInputChange = (e) => {
    const newValue = Number(e.target.value) || 0;
    if (newValue < rows) {
      const isRowAlphabet = rowFormat.includes("A");
      const isColAlphabet = columnFormat.includes("A");
      const colLabels = Array.from({ length: columns }, (_, cIndex) =>
        isColAlphabet ? getAlphabetLabel(cIndex) : `${cIndex + 1}`,
      );

      let hasBattery = false;
      for (let rIndex = newValue; rIndex < rows; rIndex += 1) {
        const rowLabel = isRowAlphabet
          ? getAlphabetLabel(rIndex)
          : `${rIndex + 1}`;
        const rowHasBattery = colLabels.some((colLabel) => {
          const cellId = `${rowLabel}${colLabel}`;
          const locker = lockers.find((item) => item.lockerId === cellId);
          return locker && Number(locker.status) !== 1;
        });
        if (rowHasBattery) {
          hasBattery = true;
          break;
        }
      }

      if (hasBattery) {
        dispatch(
          updateBatteries({ key: "cannotDeleteLockerModal", value: true }),
        );
        return;
      }
    }
    setRows(newValue);
  };

  const handleDecrementColumns = () => {
    if (columns <= 0) return;
    const isColAlphabet = columnFormat.includes("A");
    const lastColIndex = columns - 1;
    const lastColLabel = isColAlphabet
      ? getAlphabetLabel(lastColIndex)
      : `${lastColIndex + 1}`;

    const isRowAlphabet = rowFormat.includes("A");
    const rowLabels = Array.from({ length: rows }, (_, rIndex) =>
      isRowAlphabet ? getAlphabetLabel(rIndex) : `${rIndex + 1}`,
    );

    // Check if any locker in the last column contains a battery
    const colHasBattery = rowLabels.some((rowLabel) => {
      const cellId = `${rowLabel}${lastColLabel}`;
      const locker = lockers.find((item) => item.lockerId === cellId);
      return locker && Number(locker.status) !== 1; // 1 is LOCKER_STATUS.AVAILABLE
    });

    if (colHasBattery) {
      dispatch(
        updateBatteries({ key: "cannotDeleteLockerModal", value: true }),
      );
      return;
    }

    setColumns((prev) => Math.max(0, prev - 1));
  };

  const handleColumnsInputChange = (e) => {
    const newValue = Number(e.target.value) || 0;
    if (newValue < columns) {
      const isRowAlphabet = rowFormat.includes("A");
      const isColAlphabet = columnFormat.includes("A");
      const rowLabels = Array.from({ length: rows }, (_, rIndex) =>
        isRowAlphabet ? getAlphabetLabel(rIndex) : `${rIndex + 1}`,
      );

      let hasBattery = false;
      for (let cIndex = newValue; cIndex < columns; cIndex += 1) {
        const colLabel = isColAlphabet
          ? getAlphabetLabel(cIndex)
          : `${cIndex + 1}`;
        const colHasBattery = rowLabels.some((rowLabel) => {
          const cellId = `${rowLabel}${colLabel}`;
          const locker = lockers.find((item) => item.lockerId === cellId);
          return locker && Number(locker.status) !== 1;
        });
        if (colHasBattery) {
          hasBattery = true;
          break;
        }
      }

      if (hasBattery) {
        dispatch(
          updateBatteries({ key: "cannotDeleteLockerModal", value: true }),
        );
        return;
      }
    }
    setColumns(newValue);
  };

  const handleSave = () => {
    const hubToSave = id || selectedHub;
    if (!hubToSave) {
      dispatch(
        updateBatteries({
          key: "showToastMessage",
          value: {
            visible: true,
            data: {
              title: "Error",
              description: "No Hub selected.",
            },
          },
        }),
      );
      return;
    }

    dispatch(
      saveSmartContainerLayout({
        hubId: hubToSave,
        rows,
        columns,
        rowNamingFormat: rowFormat,
        colNamingFormat: columnFormat,
        disabledLockers: deletedCells,
        containerSerial: containerSerial || null,
        lockerSerials,
      }),
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        dispatch(getSmartContainerLayout({ hubId: hubToSave }));

        dispatch(
          updateBatteries({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "Success",
                description:
                  "Smart Battery Container layout saved successfully.",
              },
            },
          }),
        );
        navigate("/batteries/smart-containers");
      } else {
        const errorMsg =
          result.payload?.message || "Failed to save smart container layout.";
        dispatch(
          updateBatteries({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "Error Saving Layout",
                description: errorMsg,
              },
            },
          }),
        );
      }
    });
  };

  return (
    <div className="rounded-xl bg-white shadow-[2px_2px_8px_0px_#0000001A] m-6 mt-0 flex justify-between h-[calc(100vh-92px-72px-20px)] overflow-hidden">
      <div className="pt-4 ml-6 w-[351px] flex flex-col gap-2 overflow-y-auto pr-4">
        <img
          src="/assets/icons/battery/battery-container-2.svg"
          className="w-[52px] h-[52px]"
          alt=""
        />
        <p className="font-poppins font-medium text-base leading-none text-black">
          {id ? "Edit" : "Setup"} Container Layout
        </p>
        <p className="font-poppins font-light text-xs leading-125 text-[#525252]">
          {id ? "Edit" : "Setup"} your smart battery container to manage locker
          assignments, battery tracking, and autonomous driver access.
        </p>
        <div className="border border-light-grey rounded-2xl p-3">
          <p className="bg-yellow rounded-lg py-1.5 px-3.5 font-poppins font-medium text-sm leading-none text-black h-[35px] flex items-center">
            Step 1
          </p>
          <div className="flex flex-col mt-4">
            <p className="font-poppins font-medium text-sm leading-none text-black">
              How many columns do you want?
            </p>
            <div className="flex mt-4 gap-x-[5px]">
              <div className="flex flex-col">
                <p className="font-poppins font-medium text-xs text-dark-gray">
                  Columns
                </p>
                <div className="flex gap-x-2 mt-1.5">
                  <span
                    className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                    onClick={handleDecrementColumns}
                  >
                    <img
                      src="/assets/icons/battery/minus.svg"
                      className="w-4 h-4"
                      alt=""
                    />
                  </span>
                  <input
                    value={columns}
                    onChange={handleColumnsInputChange}
                    width={66}
                    className="w-[66px] text-center rounded-md border border-light-grey px-3.5 py-[9px] h-8 focus:border-primary focus:outline-none"
                  />
                  <span
                    className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                    onClick={() => setColumns((prev) => prev + 1)}
                  >
                    <img
                      src="/assets/icons/battery/add.svg"
                      className="w-4 h-4"
                      alt=""
                    />
                  </span>
                </div>
              </div>
              {!id && (
                <div className="flex flex-col">
                  <p className="font-poppins font-medium text-xs text-dark-gray">
                    Naming format
                  </p>
                  <div className="flex gap-x-2 mt-1.5">
                    <span
                      className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                      onClick={swapNamingFormat}
                    >
                      <img
                        src="/assets/icons/battery/arrow.svg"
                        className="w-4 h-4"
                        alt=""
                      />
                    </span>
                    <input
                      value={columnFormat}
                      readOnly
                      width={66}
                      className="w-[66px] text-center rounded-md border border-light-grey px-3.5 py-[9px] h-8 focus:border-primary focus:outline-none"
                    />
                    <span
                      className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                      onClick={swapNamingFormat}
                    >
                      <img
                        src="/assets/icons/battery/arrow.svg"
                        className="w-4 h-4 rotate-180"
                        alt=""
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col mt-6">
            <p className="font-poppins font-medium text-sm leading-none text-black">
              How many rows do you want?
            </p>
            <div className="flex mt-4 gap-x-[5px]">
              <div className="flex flex-col">
                <p className="font-poppins font-medium text-xs text-dark-gray">
                  Rows
                </p>
                <div className="flex gap-x-2 mt-1.5">
                  <span
                    className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                    onClick={handleDecrementRows}
                  >
                    <img
                      src="/assets/icons/battery/minus.svg"
                      className="w-4 h-4"
                      alt=""
                    />
                  </span>
                  <input
                    value={rows}
                    onChange={handleRowsInputChange}
                    width={66}
                    className="w-[66px] text-center rounded-md border border-light-grey px-3.5 py-[9px] h-8 focus:border-primary focus:outline-none"
                  />
                  <span
                    className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                    onClick={() => setRows((prev) => prev + 1)}
                  >
                    <img
                      src="/assets/icons/battery/add.svg"
                      className="w-4 h-4"
                      alt=""
                    />
                  </span>
                </div>
              </div>
              {!id && (
                <div className="flex flex-col">
                  <p className="font-poppins font-medium text-xs text-dark-gray">
                    Naming format
                  </p>
                  <div className="flex gap-x-2 mt-1.5">
                    <span
                      className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                      onClick={swapNamingFormat}
                    >
                      <img
                        src="/assets/icons/battery/arrow.svg"
                        className="w-4 h-4"
                        alt=""
                      />
                    </span>
                    <input
                      value={rowFormat}
                      readOnly
                      width={66}
                      className="w-[66px] text-center rounded-md border border-light-grey px-3.5 py-[9px] h-8 focus:border-primary focus:outline-none"
                    />
                    <span
                      className="bg-background hover:bg-light-grey p-2 rounded-md h-8 w-8 flex items-center justify-center cursor-pointer"
                      onClick={swapNamingFormat}
                    >
                      <img
                        src="/assets/icons/battery/arrow.svg"
                        className="w-4 h-4 rotate-180"
                        alt=""
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
            {currentStep === 1 && (
              <span
                className="w-full mt-6 rounded-md px-4 py-[11px] bg-primary-10 hover:bg-primary-20 flex gap-x-2 items-center align-middle justify-center cursor-pointer"
                onClick={() => setCurrentStep(2)}
              >
                {/* <img
                  src="/assets/icons/add.svg"
                  className="w-4 h-4 text-white"
                  alt=""
                /> */}
                <span className="text-primary font-poppins font-medium text-xs leading-none">
                  Next
                </span>
              </span>
            )}
          </div>
        </div>
        {currentStep === 2 && (
          <StepTwo
            rowFormat={rowFormat}
            rows={rows}
            columns={columns}
            columnFormat={columnFormat}
            handleSave={handleSave}
            id={id}
            setActiveSerialField={setActiveSerialField}
            containerSerial={containerSerial}
            setContainerSerial={setContainerSerial}
            lockerSerials={lockerSerials}
            setLockerSerials={setLockerSerials}
            deletedCells={deletedCells}
          />
        )}
      </div>

      <div className="rounded-xl  m-6 mt-0 flex justify-between h-[calc(100vh-92px-72px-20px)]">
        <div className="flex-1 flex justify-end items-center p-4">
          <ContainerGrid
            gridRows={rows}
            gridColumns={columns}
            lockers={lockers}
            rowFormat={rowFormat}
            columnFormat={columnFormat}
            deletedCells={deletedCells}
            setDeletedCells={setDeletedCells}
            id={id}
            activeSerialField={activeSerialField}
          />
        </div>
      </div>
      <CannotDeleteLockerModal
        visible={cannotDeleteLockerModal}
        onCancel={() =>
          dispatch(
            updateBatteries({
              key: "cannotDeleteLockerModal",
              value: false,
            }),
          )
        }
      />
    </div>
  );
}

export default SetupContainerView;
